import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * InfoDialog component props
 */
interface InfoDialogProps {
  title: string;
  message: string;
  onClose: () => void;
  buttonText?: string;
}

/**
 * InfoDialog component
 * Simple dialog for displaying informational messages with a single "Okay" button
 * Includes accessible design and proper focus management
 */
const InfoDialog: React.FC<InfoDialogProps> = ({
  title,
  message,
  onClose,
  buttonText = 'Okay'
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  /**
   * Handle keyboard events for accessibility
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      onClose();
    }
  };

  /**
   * Focus management on mount
   */
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []);

  /**
   * Prevent background scrolling when dialog is open
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const dialogContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
      }}
      onClick={onClose} // Close on backdrop click
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-message"
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '2rem',
          width: '90%',
          maxWidth: '500px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking dialog content
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Dialog Header */}
        <div style={{ marginBottom: '1rem' }}>
          <h2
            id="dialog-title"
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: '#333'
            }}
          >
            {title}
          </h2>
        </div>

        {/* Dialog Message */}
        <div
          id="dialog-message"
          style={{
            marginBottom: '1.5rem',
            color: '#666',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap' // Preserve line breaks in notes
          }}
        >
          {message}
        </div>

        {/* Dialog Action */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            ref={buttonRef}
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );

  // Render in a portal to escape any parent transforms (e.g., Drawer) so the
  // overlay covers the full viewport on desktop.
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(dialogContent, document.body);
  }

  // Fallback for non-DOM environments
  return dialogContent;
};

export default InfoDialog;

